import type { AxiosInstance } from 'axios';
import type {
  Envelope,
  CreateEnvelopeRequest,
  EnvelopeDataValue,
  EnvelopePlaceholder,
} from '../types/envelope';
import type { PaginatedResponse, PaginationParams } from '../types/common';

export function createEnvelopeService(client: AxiosInstance) {
  return {
    create: (data: CreateEnvelopeRequest) =>
      client.post<Envelope>('/v1/envelopes', data).then(r => r.data),

    getAll: (params: PaginationParams & { status?: string }) =>
      client.get<PaginatedResponse<Envelope>>('/v1/envelopes', { params }).then(r => r.data),

    /**
     * Filter-body search against the envelope index. Richer than `getAll` — accepts
     * any filter the backend's FilterSpecification exposes (status, subject, targetId,
     * senderId, createdOn, etc.). See `backend/contract-service/docs/api-endpoints.md`
     * § POST /v1/envelopes/search. Tenants are force-scoped to their org id backend-side.
     */
    search: (
      body: { filters?: Record<string, unknown>; search?: string } = {},
      params: PaginationParams = {},
    ) =>
      client
        .post<PaginatedResponse<Envelope>>('/v1/envelopes/search', body, { params })
        .then((r) => r.data),

    getById: (id: string) =>
      client.get<Envelope>(`/v1/envelopes/${id}`).then(r => r.data),

    update: (id: string, data: CreateEnvelopeRequest) =>
      client.put<Envelope>(`/v1/envelopes/${id}`, data).then(r => r.data),

    send: (id: string) =>
      client.post(`/v1/envelopes/${id}/send`),

    void: (id: string, reason: string) =>
      client.post(`/v1/envelopes/${id}/void`, { reason }),

    resend: (envelopeId: string, recipientId: string) =>
      client.post(`/v1/envelopes/${envelopeId}/resend/${recipientId}`),

    revise: (id: string) =>
      client.post(`/v1/envelopes/${id}/revise`),

    resolvePlaceholders: (id: string) =>
      client.post<EnvelopeDataValue[]>(`/v1/envelopes/${id}/resolve-placeholders`).then(r => r.data),

    updatePlaceholderValues: (id: string, values: EnvelopeDataValue[]) =>
      client.put<EnvelopeDataValue[]>(`/v1/envelopes/${id}/placeholder-values`, values).then(r => r.data),

    getPlaceholderValues: (id: string) =>
      client.get<EnvelopeDataValue[]>(`/v1/envelopes/${id}/placeholder-values`).then(r => r.data),

    /**
     * Returns the per-document placeholder *schema* for the envelope. Rows
     * are auto-seeded at create time (scanner for ad-hoc DOCX, copy from
     * catalog for template-sourced docs). Distinct from `getPlaceholderValues`,
     * which returns what the user has typed. Wizard Step 3 merges both so
     * ad-hoc DOCX uploads show their scanned placeholders once the draft is
     * saved. See `backend/contract-service/docs/api-endpoints.md` §
     * GET /v1/envelopes/{id}/placeholders.
     */
    listPlaceholders: (id: string) =>
      client
        .get<EnvelopePlaceholder[]>(`/v1/envelopes/${id}/placeholders`)
        .then((r) => r.data),

    getParticipants: (id: string) =>
      client.get<{ name: string; email: string }[]>(`/v1/envelopes/${id}/participants`).then(r => r.data),

    downloadSignedDocument: (envelopeId: string, docId: string) =>
      client.get<Blob>(`/v1/envelopes/${envelopeId}/documents/${docId}/download`, { responseType: 'blob' }),

    editAndRetrigger: (
      id: string,
      data: CreateEnvelopeRequest,
      retriggerMode: 'reset_all' | 'continue' = 'reset_all',
      send: boolean = true,
    ) =>
      client
        .put<Envelope>(
          `/v1/envelopes/${id}/edit-and-retrigger?mode=${retriggerMode}&send=${send}`,
          data,
        )
        .then((r) => r.data),
  };
}
